import { combineReducers, configureStore } from "@reduxjs/toolkit";
import { TypedUseSelectorHook, useDispatch, useSelector } from "react-redux";
import { persistReducer, persistStore } from "redux-persist";
import storage from "redux-persist/lib/storage";
import {
  apiSlice,
  currencyApiSlice,
  imageApiSlice,
  ocrApiSlice,
} from "./apis/baseQuery";
import authReducer from "./slice/auth";
import companyItemReducer from "./slice/companyItem";
import { setPersistorInstance } from "./persistorInstance";
import sessionTimeReducer from "./slice/sessionTime";
import sideNavReducer from "./slice/sideNav";
import subscriptionReducer from "./slice/subscription";
import webUtilReducer from "./slice/webUtil";
import creditReducer from "./slice/credit";
import chatReducer from "./slice/chat";

const combinedReducers = combineReducers({
  auth: authReducer,
  webUtil: webUtilReducer,
  sideNav: sideNavReducer,
  companyItem: companyItemReducer,
  subscription: subscriptionReducer,
  credit: creditReducer,
  sessionTime: sessionTimeReducer,
  chat: chatReducer,
  [apiSlice.reducerPath]: apiSlice.reducer,
  [imageApiSlice.reducerPath]: imageApiSlice.reducer,
  [currencyApiSlice.reducerPath]: currencyApiSlice.reducer,
  [ocrApiSlice.reducerPath]: ocrApiSlice.reducer,
});

// Optimize persist config - only persist critical data
const persistConfig = {
  key:
    process.env.REACT_APP_ENV_MODE === "dev"
      ? "root-dev"
      : process.env.REACT_APP_ENV_MODE === "prod"
        ? "root-prod"
        : "root-local",
  storage,
  // Use whitelist instead of blacklist for better control
  whitelist: [
    "auth", // Critical for login state
    "subscription", // Important for feature access
    "companyItem", // Company-specific settings
  ],
  // Performance optimizations
  timeout: 100, // Reduced timeout
  serialize: true,
  deserialize: true,
  // Throttle writes to storage
  throttle: 1000,
};

// Create persisted reducer with optimized config
const persistedReducer = persistReducer(persistConfig, combinedReducers);

const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [
          "persist/PERSIST",
          "persist/REHYDRATE",
          "persist/PAUSE",
          "persist/PURGE",
          "persist/FLUSH",
          "persist/REGISTER",
        ],
        ignoredActionsPaths: ["register", "rehydrate"],
        ignoredPaths: ["register"],
      },
      immutableCheck: {
        warnAfter: 32,
      },
    }).concat([
      apiSlice.middleware,
      imageApiSlice.middleware,
      currencyApiSlice.middleware,
      ocrApiSlice.middleware,
    ]),
});

// Create persistor
export const persistor = persistStore(store);
setPersistorInstance(persistor);


export default store;

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
